import React, { useState } from "react";
import { Link } from "react-router-dom";
import useCanonicalUrl from "@/hooks/useCanonicalUrl";
import { StockailyWordmark } from "@/components/brand/StockailyBrand";

/**
 * WS-11 - Electronic withdrawal declaration form.
 *
 * Submits a bounded receipt to `/api/legal/withdrawal/submit`.  The
 * confirmation MESSAGE explicitly states that we confirm RECEIPT,
 * not the final legal outcome (the withdrawal classification remains
 * an owner blocker; a conservative consumer-protective flow is used).
 * No account is required.
 */
export default function WithdrawalForm({ language = "en" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscriptionRef, setSubscriptionRef] = useState("");
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ status: "idle", receiptId: null, message: null });

  // WS-11 Correction Round — canonical URL for the withdrawal form.
  // These are the two canonical routes in App.js
  // (`/de/widerrufsformular` and `/en/withdrawal-form`).  The form is
  // never mounted from a `/legal/*` legacy route.
  useCanonicalUrl(
    language === "de" ? "/de/widerrufsformular" : "/en/withdrawal-form",
  );

  React.useEffect(() => {
    document.title = language === "de"
      ? "Muster-Widerrufsformular — Stockaily"
      : "Withdrawal form — Stockaily";
  }, [language]);

  const submit = async (ev) => {
    ev.preventDefault();
    setState({ status: "submitting", receiptId: null, message: null });
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/legal/withdrawal/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, subscription_ref: subscriptionRef, reason }),
      });
      const body = await r.json();
      if (!r.ok) {
        setState({ status: "error", receiptId: null,
                    message: body?.detail || "submission_failed" });
        return;
      }
      setState({ status: "received", receiptId: body.receipt_id,
                  message: body.confirmation_message });
    } catch {
      setState({ status: "error", receiptId: null, message: "network_error" });
    }
  };

  const t = language === "de"
    ? { title: "Muster-Widerrufsformular",
         intro: "Wenn Sie den Vertrag widerrufen möchten, füllen Sie dieses Formular aus und senden Sie es zurück.",
         email: "E-Mail-Adresse",
         name: "Name (optional)",
         subRef: "Vertrags-/Rechnungsreferenz (falls bekannt)",
         reason: "Begründung (optional)",
         submit: "Widerruf absenden",
         disclaimer: "Die Bestätigung bestätigt den Eingang Ihrer Widerrufserklärung, nicht die endgültige rechtliche Wirkung. Diese wird vom Betreiber geprüft." }
    : { title: "Model withdrawal form",
         intro: "If you wish to withdraw from the contract, fill out this form and return it.",
         email: "Email address",
         name: "Name (optional)",
         subRef: "Contract / invoice reference (if known)",
         reason: "Reason (optional)",
         submit: "Submit withdrawal",
         disclaimer: "Confirmation acknowledges receipt of your withdrawal declaration, not its final legal effect. The operator will review it." };

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      lang={language}
      data-testid={`ws11-withdrawal-shell-${language}`}
    >
      {/* Consent Foundation (2026-08-02) — parity fix: the withdrawal
          form is one of the 14 canonical legal pages and must show
          the same clickable Stockaily header as the other legal
          pages.  We reuse the exact `Stockaily` wordmark pattern from
          `LegalPage.jsx`'s `LegalShell`.  German and English both
          navigate to `/` because the app has a single locale-aware
          home route (see `App.js` — `<Route path="/" element={<Landing />} />`)
          and locale is stored under `stockaily_locale`, not encoded
          in the path.  Do not invent a new localized home route. */}
      <header
        className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur"
        data-testid="ws11-withdrawal-shell-header"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4 px-4 py-3">
          <Link
            to="/"
            aria-label="Stockaily home"
            className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 inline-flex items-center gap-2"
            data-testid="ws11-withdrawal-shell-home-link"
          >
            <StockailyWordmark height={22} />
          </Link>
          <nav aria-label="Legal language switch" className="text-sm flex items-center gap-3">
            <Link
              to={language === "de" ? "/en/withdrawal-form" : "/de/widerrufsformular"}
              className="text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded px-1 py-0.5"
              data-testid="ws11-withdrawal-shell-language-switch"
            >
              {language === "de" ? "English" : "Deutsch"}
            </Link>
          </nav>
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-6 space-y-4" data-testid="ws11-withdrawal-form-page">
      <h1 className="text-2xl font-semibold" data-testid="ws11-withdrawal-title">{t.title}</h1>
      <p className="text-sm text-muted-foreground" data-testid="ws11-withdrawal-intro">{t.intro}</p>

      <form onSubmit={submit} className="space-y-3" data-testid="ws11-withdrawal-form">
        <label className="block text-sm">
          <span>{t.email} <span aria-hidden="true">*</span></span>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full border rounded-md p-2"
                  data-testid="ws11-withdrawal-email" />
        </label>
        <label className="block text-sm">
          <span>{t.name}</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full border rounded-md p-2"
                  data-testid="ws11-withdrawal-name" />
        </label>
        <label className="block text-sm">
          <span>{t.subRef}</span>
          <input value={subscriptionRef} onChange={(e) => setSubscriptionRef(e.target.value)}
                  className="mt-1 w-full border rounded-md p-2"
                  data-testid="ws11-withdrawal-subref" />
        </label>
        <label className="block text-sm">
          <span>{t.reason}</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
                     className="mt-1 w-full border rounded-md p-2" rows={3}
                     data-testid="ws11-withdrawal-reason" />
        </label>
        <button type="submit"
                 disabled={state.status === "submitting"}
                 className="border rounded-md px-4 py-2 bg-primary text-primary-foreground"
                 data-testid="ws11-withdrawal-submit">
          {t.submit}
        </button>
      </form>

      <p className="text-xs text-muted-foreground" data-testid="ws11-withdrawal-disclaimer">
        {t.disclaimer}
      </p>

      {state.status === "received" && (
        <div className="border rounded-md p-3 bg-green-50 text-green-900"
              role="status" data-testid="ws11-withdrawal-receipt">
          <strong>Receipt ID: {state.receiptId}</strong>
          <p className="text-sm mt-1">{state.message}</p>
        </div>
      )}
      {state.status === "error" && (
        <div className="border rounded-md p-3 bg-red-50 text-red-900"
              role="alert" data-testid="ws11-withdrawal-error">
          {state.message}
        </div>
      )}
      </main>
    </div>
  );
}
